import React, { useState, useEffect, useCallback } from 'react';
import { listJobs, getJobFiles, restoreFromJob } from '../services/api';
import { useToast } from '../hooks/useToast';
import { TableRowSkeleton } from './Skeleton';

const TERMINAL = ['done', 'failed', 'budget_exceeded', 'escalated', 'security_blocked', 'pm_spec_invalid', 'insufficient_credits', 'cancelled', 'needs_attention'];

const STATUS_META = {
  done:             { color: '#22c55e', label: 'Done' },
  failed:           { color: '#ef4444', label: 'Failed' },
  security_blocked: { color: '#ef4444', label: 'Blocked' },
  budget_exceeded:  { color: '#ef4444', label: 'Budget' },
  insufficient_credits: { color: '#f59e0b', label: 'No Credits' },
  cancelled:         { color: '#6b7280', label: 'Cancelled' },
  executing:        { color: '#f59e0b', label: 'Running' },
  planning:         { color: '#6366f1', label: 'Planning' },
  architecting:     { color: '#8b5cf6', label: 'Designing' },
  validating:       { color: '#3b82f6', label: 'Validating' },
  testing:          { color: '#3b82f6', label: 'Testing' },
  deploying:        { color: '#f59e0b', label: 'Deploying' },
  pending:          { color: 'var(--text-muted)', label: 'Pending' },
};

const COLS = '2fr 100px 80px 75px 75px 80px 120px 110px';

function duration(job) {
  if (!job.updated_at || !job.created_at) return '—';
  const secs = Math.max(0, Math.round((new Date(job.updated_at) - new Date(job.created_at)) / 1000));
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function Row({ job, projectId, onPreview, onRestored }) {
  const toast = useToast();
  const [restoring, setRestoring] = useState(false);
  const [restored, setRestored] = useState(false);
  const [hovered, setHovered] = useState(false);

  // WEAK-MODEL-VISIBILITY-FIX: job.status === 'done' alone doesn't mean the
  // real build actually verified — build_verifier.py fails OPEN, so an
  // exhausted-retries job still lands here as "done". Flag it distinctly
  // instead of a green checkmark that implies a clean build.
  // GRACEFUL-DEGRADE-FIX: build_degraded_files is a lesser, distinct case
  // — the build DOES compile, but one or more pages were swapped for a
  // placeholder to get there.
  const meta = job.status === 'done' && job.build_incomplete
    ? { color: '#f59e0b', label: 'Incomplete' }
    : job.status === 'done' && job.build_degraded_files
      ? { color: '#f59e0b', label: 'Partial' }
      : (STATUS_META[job.status] || { color: 'var(--text-muted)', label: job.status });
  const isRunning = !TERMINAL.includes(job.status);
  const prompt = job.prompt || job.title || 'Untitled';

  const handlePreview = async (e) => {
    e.stopPropagation();
    try {
      const data = await getJobFiles(job.id);
      if (data.files && Object.keys(data.files).length > 0) onPreview && onPreview(data.files);
    } catch (e) {}
  };

  const handleRestore = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Restore project to this generation?\n\n"${prompt}"\n\nCurrent files will be auto-saved as a snapshot first.`)) return;
    setRestoring(true);
    try {
      await restoreFromJob(projectId, job.id);
      setRestored(true);
      toast.success('Project restored to this version!');
      onRestored && onRestored();
    } catch (err) {
      toast.error('Restore failed: ' + err.message);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div
      style={{ ...s.row, ...(hovered ? s.rowHover : {}), ...(isRunning ? { borderLeft: `2px solid ${meta.color}` } : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Prompt — truncated with native title tooltip */}
      <div style={s.cell} title={prompt}>
        <span style={s.promptText}>{prompt}</span>
      </div>

      {/* Status */}
      <div style={{ ...s.cell, justifyContent: 'center' }}>
        <span style={{ ...s.statusPill, color: meta.color, borderColor: meta.color + '55', background: meta.color + '12' }}>
          {isRunning && <span style={{ ...s.dot, background: meta.color }} />}
          {meta.label}
        </span>
      </div>

      {/* Tokens */}
      <div style={{ ...s.cell, justifyContent: 'center', color: 'var(--text-muted)' }}>
        {job.tokens_used > 0 ? job.tokens_used.toLocaleString() : '—'}
      </div>

      {/* Input tokens */}
      <div style={{ ...s.cell, justifyContent: 'center', color: 'var(--text-muted)', fontSize: 11 }} title="Prompt (input) tokens">
        {job.prompt_tokens_used > 0 ? job.prompt_tokens_used.toLocaleString() : '—'}
      </div>

      {/* Output tokens */}
      <div style={{ ...s.cell, justifyContent: 'center', color: 'var(--text-muted)', fontSize: 11 }} title="Completion (output) tokens">
        {job.completion_tokens_used > 0 ? job.completion_tokens_used.toLocaleString() : '—'}
      </div>

      {/* Cost */}
      <div style={{ ...s.cell, justifyContent: 'center', color: 'var(--text-muted)' }}>
        {job.cost_usd > 0 ? `$${job.cost_usd.toFixed(4)}` : '—'}
      </div>

      {/* Duration */}
      <div style={{ ...s.cell, justifyContent: 'center', color: 'var(--text-muted)' }}>
        {duration(job)}
      </div>

      {/* Created */}
      <div style={{ ...s.cell, justifyContent: 'center', color: 'var(--text-muted)', fontSize: 11 }}>
        {new Date(job.created_at).toLocaleDateString()} {new Date(job.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>

      {/* Actions */}
      <div style={{ ...s.cell, justifyContent: 'center', gap: 4 }}>
        {job.status === 'done' && (
          <>
            <button onClick={handlePreview} style={s.btn} title="Preview files">👁</button>
            <button onClick={handleRestore} disabled={restoring} style={{ ...s.btn, ...(restored ? s.btnDone : {}) }} title="Restore to this version">
              {restored ? '✅' : restoring ? '⏳' : '↩'}
            </button>
          </>
        )}
        {job.pr_url && (
          <a href={job.pr_url} target="_blank" rel="noopener noreferrer" style={s.linkBtn} title="Open PR" onClick={e => e.stopPropagation()}>PR</a>
        )}
        {job.deploy_url && (
          <a href={job.deploy_url} target="_blank" rel="noopener noreferrer" style={{ ...s.linkBtn, color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' }} title="Open Deploy" onClick={e => e.stopPropagation()}>🚀</a>
        )}
      </div>
    </div>
  );
}

export default function IterationHistory({ projectId, onPreviewFiles, onRestored }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const startedAt = Date.now();
    const MIN_SKELETON_MS = 350;
    try {
      const data = await listJobs(projectId);
      setJobs(data);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load history');
    } finally {
      const elapsed = Date.now() - startedAt;
      const wait = Math.max(0, MIN_SKELETON_MS - elapsed);
      setTimeout(() => setLoading(false), wait);
    }
  }, [projectId]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) {
    return (
      <div style={s.wrapper} className="skeleton-fade-in">
        <div style={s.thead}>
          <div style={s.th}>Prompt</div>
          <div style={{ ...s.th, textAlign: 'center' }}>Status</div>
          <div style={{ ...s.th, textAlign: 'center' }}>Tokens</div>
          <div style={{ ...s.th, textAlign: 'center' }}>Input</div>
          <div style={{ ...s.th, textAlign: 'center' }}>Output</div>
          <div style={{ ...s.th, textAlign: 'center' }}>Cost</div>
          <div style={{ ...s.th, textAlign: 'center' }}>Duration</div>
          <div style={{ ...s.th, textAlign: 'center' }}>Created</div>
          <div style={{ ...s.th, textAlign: 'center' }}>Actions</div>
        </div>
        <div style={s.tbody}>
          {[1, 2, 3, 4, 5].map(i => <TableRowSkeleton key={i} />)}
        </div>
      </div>
    );
  }
  if (error) return <div style={{ ...s.center, color: '#ef4444' }}>⚠ {error}</div>;
  if (jobs.length === 0) return (
    <div style={s.empty}>
      <span style={{ fontSize: 32, opacity: 0.2 }}>💬</span>
      <p style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: 12 }}>No generations yet.</p>
    </div>
  );

  return (
    <div style={s.wrapper}>
      {/* Table header */}
      <div style={s.thead}>
        <div style={s.th}>Prompt</div>
        <div style={{ ...s.th, textAlign: 'center' }}>Status</div>
        <div style={{ ...s.th, textAlign: 'center' }}>Tokens</div>
        <div style={{ ...s.th, textAlign: 'center' }}>Input</div>
        <div style={{ ...s.th, textAlign: 'center' }}>Output</div>
        <div style={{ ...s.th, textAlign: 'center' }}>Cost</div>
        <div style={{ ...s.th, textAlign: 'center' }}>Duration</div>
        <div style={{ ...s.th, textAlign: 'center' }}>Created</div>
        <div style={{ ...s.th, textAlign: 'center' }}>Actions</div>
      </div>

      {/* Rows */}
      <div style={s.tbody}>
        {jobs.map(job => (
          <Row
            key={job.id}
            job={job}
            projectId={projectId}
            onPreview={onPreviewFiles}
            onRestored={onRestored}
          />
        ))}
      </div>
    </div>
  );
}

const s = {
  wrapper: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  center: { padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 },

  thead: {
    display: 'grid', gridTemplateColumns: COLS,
    padding: '8px 12px', flexShrink: 0,
    borderBottom: '1px solid var(--border)',
    background: 'var(--table-header-bg)',
  },
  th: { fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' },

  tbody: { flex: 1, overflowY: 'auto' },

  row: {
    display: 'grid', gridTemplateColumns: COLS,
    padding: '9px 12px', alignItems: 'center',
    borderBottom: '1px solid var(--bg-card)',
    borderLeft: '2px solid transparent',
    cursor: 'default', transition: 'background 0.12s',
  },
  rowHover: { background: 'rgba(99,102,241,0.05)' },

  cell: { display: 'flex', alignItems: 'center', fontSize: 12, minWidth: 0, overflow: 'hidden' },

  promptText: {
    color: 'var(--text-primary)', fontWeight: 500, fontSize: 12,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    maxWidth: '100%',
  },

  statusPill: {
    fontSize: 10, fontWeight: 600, padding: '2px 8px',
    borderRadius: 20, border: '1px solid',
    display: 'flex', alignItems: 'center', gap: 4,
    whiteSpace: 'nowrap',
  },
  dot: { width: 5, height: 5, borderRadius: '50%', flexShrink: 0, animation: 'pulse 1.2s infinite' },

  btn: {
    width: 24, height: 24, border: '1px solid var(--border)',
    borderRadius: 5, background: 'var(--bg-card)',
    color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  btnDone: { color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' },
  linkBtn: {
    fontSize: 10, fontWeight: 600, padding: '2px 6px',
    border: '1px solid rgba(99,102,241,0.3)', borderRadius: 5,
    color: 'var(--accent-light)', textDecoration: 'none', whiteSpace: 'nowrap',
  },
};
